import * as React from 'react'
import { useMutation, useQuery } from '@apollo/react-hooks'
import CloudinaryImage from 'web-makeup/dist/CloudinaryImage'
import { useFormik } from 'formik'
import { Col } from 'antd'
import { colors } from 'web-makeup/dist/design-system/config/data/theme'

import { GET_COUNTRIES } from '../../../../../queries/GetCountries'
import { UPDATE_BRAND_PROFILE } from '../../../../../mutations/UpdateBrandProfile'
import { client as apolloClient } from '../../../../ApolloProvider/ApolloProvider'
import { topLocations } from '../../../../../helpers/top-locations-helper'
import { Brand as BrandType } from '../../../../../typings/types'
import LocationForm from '../../../../../components/LocationForm/LocationForm'

import DefaultButton from '../../../components/DefaultButton/DefaultButton'
import LogoUpload from '../../../components/LogoUpload/LogoUpload'
import { blackListedCountry, topListedCountry } from '../../config'

import {
  PageTitle,
  WhiteBox,
  BrandInfo,
  Info,
  LogoWrapper,
  InputLabel,
  Input,
  ButtonWrapper,
  StyledRow,
} from '../../BrandDashboardSettings.style'

interface BrandDetailsFormProps {
  brandProfile?: BrandType
  brandOrderUuid?: string
}

const BrandDetailsForm: React.FC<BrandDetailsFormProps> = ({ brandProfile, brandOrderUuid }) => {
  const [cityUuid, setCityUuid] = React.useState('')
  const [countryUuid, setCountryUuid] = React.useState('')
  const [countryName, setCountryName] = React.useState('')
  const [cityName, setCityName] = React.useState('')
  const [avatarState, setAvatarState] = React.useState('')
  const [brandDetailsInitialState, useBrandDetailsInitialState] = React.useState({
    name: '',
    website: '',
    address: '',
    phone: '',
    country: '',
    city: '',
    avatar: '',
  })

  const [updateBrandProfile, { loading: brandDetailsUpdating }] = useMutation(
    UPDATE_BRAND_PROFILE,
    {
      client: apolloClient,
    }
  )

  const { data: countriesData } = useQuery(GET_COUNTRIES, {
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
    client: apolloClient,
  })

  const { videoProjectsCount } = brandProfile || {}
  const disableLocation = videoProjectsCount && videoProjectsCount > 0

  React.useEffect(
    () => {
      if (brandProfile) {
        brandProfile.avatar &&
          brandProfile.avatar.cloudinaryId &&
          setAvatarState(brandProfile.avatar.cloudinaryId)
        if (brandProfile.country) {
          setCountryName(brandProfile.country.name)
          setCountryUuid(brandProfile.country.uuid)
        }
        brandProfile.city &&
          setCityName(fullCityName(brandProfile.city.name, brandProfile.city.subdivision1Name))
        useBrandDetailsInitialState({
          name: brandProfile.tradingName || '',
          website: brandProfile.website || '',
          address: brandProfile.address || '',
          phone: brandProfile.mainPhone || '',
          country:
            (brandProfile.city && brandProfile.city.country.uuid) ||
            (brandProfile.country && brandProfile.country.uuid) ||
            '',
          city: (brandProfile.city && brandProfile.city.uuid) || '',
          avatar: (brandProfile.avatar && brandProfile.avatar.cloudinaryId) || '',
        })
      }
    },
    [brandProfile]
  )

  const isCountryDisabled = brandProfile && brandProfile.videoProjectsCount !== 0

  React.useEffect(
    () => {
      brandDetailsformik.setFieldValue('city', cityUuid)
      !isCountryDisabled && brandDetailsformik.setFieldValue('country', countryUuid)
    },
    [cityUuid, countryUuid]
  )
  React.useEffect(
    () => {
      brandDetailsformik.setFieldValue('avatar', avatarState)
    },
    [avatarState]
  )

  const fullCityName = (cityFirstName, stateName): string => {
    const names: string[] = [cityFirstName]
    if (stateName !== cityFirstName && stateName !== null) {
      names.push(stateName)
    }
    return names.join(', ')
  }
  const countiesList = countriesData && countriesData.allCountries
  const filteredCounryData =
    countiesList &&
    countiesList.filter(obj => {
      return !blackListedCountry.some(v => obj.name === v)
    })
  filteredCounryData &&
    topListedCountry
      .slice(0)
      .reverse()
      .map(item => topLocations(item, filteredCounryData))

  const brandDetailsformik = useFormik({
    initialValues: {
      ...brandDetailsInitialState,
    },
    enableReinitialize: true,
    onSubmit: values => {
      updateBrandProfile({
        variables: {
          brandUuid: brandOrderUuid,
          brandUpdateInput: {
            name: values.name,
            website: values.website,
            address: values.address,
            mainPhone: values.phone,
            cityUuid: values.city,
            countryID: values.country,
            avatar: { cloudinaryId: avatarState, cloudinaryType: 'fetch' },
          },
        },
      })
    },
  })

  return (
    <>
      <PageTitle>Brand details</PageTitle>
      <form onSubmit={brandDetailsformik.handleSubmit}>
        <WhiteBox>
          <BrandInfo>
            <LogoWrapper>
              <CloudinaryImage
                useSourceFormat
                source={{
                  type:
                    `fetch` ||
                    (brandProfile && brandProfile.avatar && brandProfile.avatar.cloudinaryType) ||
                    `upload`,
                  id:
                    avatarState ||
                    (brandProfile && brandProfile.avatar && brandProfile.avatar.cloudinaryId) ||
                    `v1607012790/-design-system/huawei-icon.jpg`,
                }}
              />
            </LogoWrapper>
            <Info>
              <h3>Your brand logo</h3>
              <p>
                Your brand picture is an important element of your 90 Seconds presence. Square JPG
                or PNG images (without transparency) work best.
              </p>
              <LogoUpload
                onSuccess={e => {
                  e && e.url && setAvatarState(e.url)
                }}
              />
            </Info>
          </BrandInfo>
          <StyledRow type="flex" gutter={24}>
            <Col xs={24} sm={24} md={12} lg={12}>
              <InputLabel>Name</InputLabel>
              <Input
                className={brandDetailsformik.values.name === '' ? '' : 'filled'}
                type="text"
                placeholder="Name"
                name="name"
                onChange={brandDetailsformik.handleChange}
                value={brandDetailsformik.values.name}
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <InputLabel>Website</InputLabel>
              <Input
                className={brandDetailsformik.values.website === '' ? '' : 'filled'}
                type="text"
                placeholder="Website"
                name="website"
                onChange={brandDetailsformik.handleChange}
                value={brandDetailsformik.values.website}
              />
            </Col>
          </StyledRow>
          <StyledRow type="flex">
            <Col xs={24} sm={24} md={24} lg={24}>
              <InputLabel>Address</InputLabel>
              <Input
                className={brandDetailsformik.values.address === '' ? '' : 'filled'}
                type="text"
                placeholder="Address"
                name="address"
                onChange={brandDetailsformik.handleChange}
                value={brandDetailsformik.values.address}
              />
            </Col>
          </StyledRow>
          <StyledRow type="flex" gutter={24}>
            <Col xs={24} sm={24} md={12} lg={12}>
              <InputLabel>Phone number</InputLabel>
              <Input
                className={brandDetailsformik.values.phone === '' ? '' : 'filled'}
                type="text"
                placeholder="Phone number"
                name="phone"
                onChange={brandDetailsformik.handleChange}
                value={brandDetailsformik.values.phone}
              />
            </Col>
          </StyledRow>
          <LocationForm
            setCountryId={e => {
              setCountryName('')
              setCountryUuid(e)
            }}
            setCityId={e => {
              setCityName('')
              setCityUuid(e)
            }}
            initialCountryId={countryUuid}
            isCoutryDisabled={disableLocation || false}
            error={false}
            countryDisplayValue={countryName}
            cityDisplayValue={cityName}
          />
          <ButtonWrapper>
            <DefaultButton
              fontSize="1rem"
              padding="0 1.625rem"
              backgroundColor={colors.adaptBlue}
              style={{ marginLeft: 'auto' }}
              type="submit"
              disabled={brandDetailsUpdating}
            >
              {brandDetailsUpdating ? 'Loading...' : 'Save changes'}
            </DefaultButton>
          </ButtonWrapper>
        </WhiteBox>
      </form>
    </>
  )
}

export default BrandDetailsForm



// WEBPACK FOOTER //
// ./src/components/BrandDashboard/BrandDashboardSettings/forms/BrandDetailsForm/BrandDetailsForm.tsx